import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './auth-context';
import { accountsAPI } from './api';

const ProfileContext = createContext(null);

export function ProfileProvider({ children }) {
  const { user } = useAuth();
  const prevUserIdRef = useRef(user?.id != null ? String(user.id) : null);

  const [accountId, setAccountIdState] = useState(
    () => localStorage.getItem('protego_account_id') || null
  );
  const [profileId, setProfileIdState] = useState(
    () => localStorage.getItem('protego_profile_id') || null
  );
  const [accounts, setAccounts] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  useEffect(() => {
    const currentUserId = user?.id != null ? String(user.id) : null;
    const prevUserId = prevUserIdRef.current;

    if (prevUserId && !currentUserId) {
      // User logged out — clear everything
      localStorage.removeItem('protego_account_id');
      localStorage.removeItem('protego_profile_id');
      setAccountIdState(null);
      setProfileIdState(null);
      setAccounts([]);
      setProfiles([]);
      setCurrentAccount(null);
      setCurrentProfile(null);
    } else if (!prevUserId && currentUserId) {
      // Fresh login — force explicit account/profile selection for this session.
      localStorage.removeItem('protego_account_id');
      localStorage.removeItem('protego_profile_id');
      setAccountIdState(null);
      setProfileIdState(null);
      setProfiles([]);
      setCurrentAccount(null);
      setCurrentProfile(null);
    } else if (currentUserId && prevUserId !== null && prevUserId !== currentUserId) {
      // Another user signed in (not initial session load after refresh) — clear stale selection
      localStorage.removeItem('protego_account_id');
      localStorage.removeItem('protego_profile_id');
      setAccountIdState(null);
      setProfileIdState(null);
      setProfiles([]);
      setCurrentAccount(null);
      setCurrentProfile(null);
    }

    prevUserIdRef.current = currentUserId;
  }, [user]);

  const loadAccounts = useCallback(async () => {
    if (!user) return;
    try {
      const res = await accountsAPI.getAll();
      const data = res.data?.data || res.data || [];
      setAccounts(Array.isArray(data) ? data : []);
    } catch {
      setAccounts([]);
    }
  }, [user]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const setAccountId = useCallback((id) => {
    if (id) {
      localStorage.setItem('protego_account_id', id);
    } else {
      localStorage.removeItem('protego_account_id');
    }
    setAccountIdState(id);
  }, []);

  const setProfileId = useCallback((id) => {
    if (id) {
      localStorage.setItem('protego_profile_id', id);
    } else {
      localStorage.removeItem('protego_profile_id');
    }
    setProfileIdState(id);
  }, []);

  const switchAccount = useCallback((accId) => {
    setAccountId(accId);
    setProfileId(null);
    setProfiles([]);
    setCurrentProfile(null);
  }, [setAccountId, setProfileId]);

  const selectProfile = useCallback((accId, profId) => {
    setAccountId(accId);
    setProfileId(profId);
  }, [setAccountId, setProfileId]);

  const clearSelection = useCallback(() => {
    setAccountId(null);
    setProfileId(null);
    setProfiles([]);
    setCurrentProfile(null);
    setCurrentAccount(null);
  }, [setAccountId, setProfileId]);

  const hasSelection = Boolean(accountId && profileId);

  const loadProfiles = useCallback(async (accId) => {
    const targetId = accId || accountId;
    if (!targetId) return;
    setLoadingProfiles(true);
    try {
      const res = await accountsAPI.getProfiles(targetId);
      const data = res.data?.data || res.data || [];
      setProfiles(Array.isArray(data) ? data : []);
    } catch {
      setProfiles([]);
    } finally {
      setLoadingProfiles(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (accountId) {
      loadProfiles(accountId);
    }
  }, [accountId, loadProfiles]);

  useEffect(() => {
    if (accounts.length > 0 && accountId) {
      const found = accounts.find(a => String(a.id) === String(accountId));
      setCurrentAccount(found || null);
    } else {
      setCurrentAccount(null);
    }
  }, [accounts, accountId]);

  useEffect(() => {
    if (profiles.length > 0 && profileId) {
      const found = profiles.find(p => String(p.id) === String(profileId));
      setCurrentProfile(found || null);
    } else {
      setCurrentProfile(null);
    }
  }, [profiles, profileId]);

  return (
    <ProfileContext.Provider value={{
      accountId,
      profileId,
      accounts,
      profiles,
      currentAccount,
      currentProfile,
      loadingProfiles,
      hasSelection,
      setAccountId,
      setProfileId,
      switchAccount,
      selectProfile,
      clearSelection,
      loadAccounts,
      loadProfiles,
    }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context) throw new Error('useProfile must be used within ProfileProvider');
  return context;
}
